function toggleHidden(element, isHidden) {
  if (!element) return;
  element.toggleAttribute('hidden', isHidden);
}

/**
 * Decode HTML entities in strings read from data-* attributes.
 * Needed when a translation (or double | escape) leaves literal &#39; etc.
 */
function decodeHtmlEntities(value) {
  if (!value || typeof value !== 'string' || !value.includes('&')) {
    return value || '';
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function amountsMatch(firstMoney, secondMoney) {
  if (!firstMoney || !secondMoney) return false;

  return (
    firstMoney.amount === secondMoney.amount &&
    firstMoney.currencyCode === secondMoney.currencyCode
  );
}

function formatMoney(money) {
  if (!money?.amount || !money?.currencyCode) return '';

  const numericAmount = Number(money.amount);
  if (Number.isNaN(numericAmount)) {
    return `${money.amount} ${money.currencyCode}`;
  }

  const locale = document.documentElement.lang || undefined;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: money.currencyCode,
    }).format(numericAmount);
  } catch (error) {
    return `${money.amount} ${money.currencyCode}`;
  }
}

function formatPriceRange(
  priceRange,
  minKey = 'minVariantPrice',
  maxKey = 'maxVariantPrice',
) {
  const minPrice = formatMoney(priceRange?.[minKey]);
  const maxPrice = formatMoney(priceRange?.[maxKey]);

  if (!minPrice) return '';
  if (!maxPrice || amountsMatch(priceRange?.[minKey], priceRange?.[maxKey])) {
    return minPrice;
  }

  return `${minPrice} - ${maxPrice}`;
}

function productUrlFor(product) {
  if (!product?.handle) return product?.onlineStoreUrl || '#';

  // Locale/market-aware root (e.g. "/", "/fr/", "/nl/") — do not use Admin onlineStoreUrl
  // which is the primary-locale absolute URL.
  const root = window.Shopify?.routes?.root || '/';
  return `${root}products/${product.handle}`;
}

/** Match `snippets/card-product.liquid` srcset breakpoints (Shopify CDN `width=`). */
const WISHLIST_IMAGE_SRCSET_WIDTHS = [165, 360, 533, 720, 940, 1066];

function shopifyImageUrlWithWidth(baseUrl, pixelWidth) {
  if (!baseUrl) return '';
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}width=${pixelWidth}`;
}

function wishlistFeaturedImageSrcSet(baseUrl, naturalWidth) {
  if (!baseUrl) return '';

  const widths = WISHLIST_IMAGE_SRCSET_WIDTHS.filter(
    (w) => naturalWidth == null || naturalWidth >= w,
  );
  const seenDescriptors = new Set(widths.map((w) => String(w)));
  const parts = widths.map(
    (w) => `${shopifyImageUrlWithWidth(baseUrl, w)} ${w}w`,
  );

  if (
    naturalWidth != null &&
    naturalWidth > 0 &&
    !seenDescriptors.has(String(naturalWidth))
  ) {
    parts.push(
      `${shopifyImageUrlWithWidth(baseUrl, naturalWidth)} ${naturalWidth}w`,
    );
  }

  return parts.join(', ');
}

function formatModelFamilyTitle(handle) {
  const spaced = String(handle ?? '').replace(/-/g, ' ');
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Guest pending entries — shared with guest wishlist claim flow. */
const GUEST_WISHLIST_STORAGE_KEY = 'noos:wishlist:pendingHandles';

/** Logged-in optimistic add/remove overlay — survives same-tab navigation. */
const OPTIMISTIC_WISHLIST_OVERLAY_KEY = 'noos:wishlist:optimisticOverlay';

/** Liquid handles snapshot for the current page (before overlay merge). */
let liquidWishlistFamilyHandlesSnapshot = null;

/** Dedupe background retries for overlay ops still pending after navigation. */
const optimisticOverlayRetryInflight = new Map();

function nullableDatasetString(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function guestHandleKey(handle) {
  return String(handle || '')
    .trim()
    .toLowerCase();
}

function guestHandlesInclude(handles, handle) {
  const key = guestHandleKey(handle);
  if (!key) return false;
  return (Array.isArray(handles) ? handles : []).some(
    (item) => guestHandleKey(item) === key,
  );
}

function normalizeGuestWishlistEntry(item) {
  if (typeof item === 'string') {
    const familyHandle = item.trim();
    if (!familyHandle) return null;
    return {
      familyHandle,
      vendor: null,
      productId: null,
      variantId: null,
      sku: null,
    };
  }

  if (!item || typeof item !== 'object') return null;
  const familyHandle = String(item.familyHandle || '').trim();
  if (!familyHandle) return null;

  return {
    familyHandle,
    vendor: nullableDatasetString(item.vendor),
    productId: nullableDatasetString(item.productId),
    variantId: nullableDatasetString(item.variantId),
    sku: nullableDatasetString(item.sku),
  };
}

function getGuestWishlistEntries() {
  try {
    const raw = window.localStorage?.getItem(GUEST_WISHLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const byHandle = new Map();
    for (const item of parsed) {
      const entry = normalizeGuestWishlistEntry(item);
      if (!entry) continue;
      byHandle.set(guestHandleKey(entry.familyHandle), entry);
    }
    return Array.from(byHandle.values());
  } catch (error) {
    return [];
  }
}

function getGuestWishlistHandles() {
  return getGuestWishlistEntries().map((entry) => entry.familyHandle);
}

function setGuestWishlistEntries(entries) {
  const previous = getGuestWishlistEntries();
  const byHandle = new Map();
  for (const item of Array.isArray(entries) ? entries : []) {
    const entry = normalizeGuestWishlistEntry(item);
    if (!entry) continue;
    byHandle.set(guestHandleKey(entry.familyHandle), entry);
  }
  const unique = Array.from(byHandle.values());

  try {
    if (unique.length === 0) {
      window.localStorage?.removeItem(GUEST_WISHLIST_STORAGE_KEY);
    } else {
      window.localStorage?.setItem(
        GUEST_WISHLIST_STORAGE_KEY,
        JSON.stringify(unique),
      );
    }
  } catch (error) {
    console.error(error);
    // Storage blocked/full — do not fake a badge update.
    return previous;
  }

  setWishlistFamilyCount(unique.length);
  return unique;
}

function setGuestWishlistHandles(handles) {
  return setGuestWishlistEntries(
    (Array.isArray(handles) ? handles : []).map((handle) => ({
      familyHandle: handle,
    })),
  ).map((entry) => entry.familyHandle);
}

function addGuestWishlistEntry(entryInput) {
  const entry = normalizeGuestWishlistEntry(entryInput);
  if (!entry) return getGuestWishlistEntries();
  const key = guestHandleKey(entry.familyHandle);
  const next = getGuestWishlistEntries().filter(
    (item) => guestHandleKey(item.familyHandle) !== key,
  );
  next.push(entry);
  return setGuestWishlistEntries(next);
}

function addGuestWishlistHandle(handle) {
  return addGuestWishlistEntry({ familyHandle: handle }).map(
    (entry) => entry.familyHandle,
  );
}

function removeGuestWishlistHandle(handle) {
  const key = guestHandleKey(handle);
  return setGuestWishlistEntries(
    getGuestWishlistEntries().filter(
      (item) => guestHandleKey(item.familyHandle) !== key,
    ),
  ).map((entry) => entry.familyHandle);
}

function syncWishlistFamilyHandlesCache(handle, mode) {
  const trimmed = String(handle || '').trim();
  if (!trimmed) return;

  const current = Array.isArray(window.wishlistFamilyHandles)
    ? window.wishlistFamilyHandles
    : [];
  const key = guestHandleKey(trimmed);

  if (mode === 'add') {
    if (!current.some((item) => guestHandleKey(item) === key)) {
      window.wishlistFamilyHandles = [...current, trimmed];
    }
    return;
  }

  window.wishlistFamilyHandles = current.filter(
    (item) => guestHandleKey(item) !== key,
  );
}

function uniqueWishlistHandles(...lists) {
  const byKey = new Map();
  for (const list of lists) {
    for (const handle of Array.isArray(list) ? list : []) {
      const trimmed = String(handle || '').trim();
      if (!trimmed) continue;
      const key = guestHandleKey(trimmed);
      if (!byKey.has(key)) byKey.set(key, trimmed);
    }
  }
  return Array.from(byKey.values());
}

function emptyOptimisticOverlay() {
  return { add: [], remove: [], settled: [] };
}

function normalizeHandleList(list, seen) {
  const result = [];
  for (const handle of Array.isArray(list) ? list : []) {
    const trimmed = String(handle || '').trim();
    if (!trimmed) continue;
    const key = guestHandleKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

function normalizeOptimisticOverlay(raw) {
  const overlay = emptyOptimisticOverlay();
  if (!raw || typeof raw !== 'object') return overlay;

  const seenAdd = new Set();
  const seenRemove = new Set();
  const seenSettled = new Set();

  overlay.add = normalizeHandleList(raw.add, seenAdd);
  overlay.remove = normalizeHandleList(raw.remove, seenRemove).filter((handle) => {
    const key = guestHandleKey(handle);
    return !seenAdd.has(key);
  });
  overlay.settled = normalizeHandleList(raw.settled, seenSettled).filter(
    (handle) => {
      const key = guestHandleKey(handle);
      return seenAdd.has(key) || seenRemove.has(key);
    },
  );

  return overlay;
}

function getOptimisticOverlay() {
  try {
    const raw = window.sessionStorage?.getItem(OPTIMISTIC_WISHLIST_OVERLAY_KEY);
    if (!raw) return emptyOptimisticOverlay();
    return normalizeOptimisticOverlay(JSON.parse(raw));
  } catch (error) {
    return emptyOptimisticOverlay();
  }
}

function setOptimisticOverlay(overlayInput) {
  const overlay = normalizeOptimisticOverlay(overlayInput);
  try {
    if (
      overlay.add.length === 0 &&
      overlay.remove.length === 0 &&
      overlay.settled.length === 0
    ) {
      window.sessionStorage?.removeItem(OPTIMISTIC_WISHLIST_OVERLAY_KEY);
    } else {
      window.sessionStorage?.setItem(
        OPTIMISTIC_WISHLIST_OVERLAY_KEY,
        JSON.stringify(overlay),
      );
    }
  } catch (error) {
    console.error(error);
  }
  return overlay;
}

function recordOptimisticOverlay(handle, mode) {
  const trimmed = String(handle || '').trim();
  if (!trimmed || (mode !== 'add' && mode !== 'remove')) {
    return getOptimisticOverlay();
  }

  const key = guestHandleKey(trimmed);
  const overlay = getOptimisticOverlay();
  const next = emptyOptimisticOverlay();

  if (mode === 'add') {
    next.add = [
      ...overlay.add.filter((item) => guestHandleKey(item) !== key),
      trimmed,
    ];
    next.remove = overlay.remove.filter((item) => guestHandleKey(item) !== key);
  } else {
    next.remove = [
      ...overlay.remove.filter((item) => guestHandleKey(item) !== key),
      trimmed,
    ];
    next.add = overlay.add.filter((item) => guestHandleKey(item) !== key);
  }

  // New user action — not settled until wishlist-update succeeds.
  next.settled = overlay.settled.filter((item) => guestHandleKey(item) !== key);

  return setOptimisticOverlay(next);
}

function markOptimisticOverlaySettled(handle) {
  const trimmed = String(handle || '').trim();
  const key = guestHandleKey(trimmed);
  if (!key) return getOptimisticOverlay();

  const overlay = getOptimisticOverlay();
  const inFlight =
    guestHandlesInclude(overlay.add, trimmed) ||
    guestHandlesInclude(overlay.remove, trimmed);
  if (!inFlight) return overlay;

  return setOptimisticOverlay({
    add: overlay.add,
    remove: overlay.remove,
    settled: [
      ...overlay.settled.filter((item) => guestHandleKey(item) !== key),
      trimmed,
    ],
  });
}

function clearOptimisticOverlay(handle) {
  const key = guestHandleKey(handle);
  if (!key) return getOptimisticOverlay();

  const overlay = getOptimisticOverlay();
  return setOptimisticOverlay({
    add: overlay.add.filter((item) => guestHandleKey(item) !== key),
    remove: overlay.remove.filter((item) => guestHandleKey(item) !== key),
    settled: overlay.settled.filter((item) => guestHandleKey(item) !== key),
  });
}

function applyOptimisticOverlayToHandles(baseHandles) {
  const overlay = getOptimisticOverlay();
  let next = uniqueWishlistHandles(baseHandles);

  for (const handle of overlay.add) {
    next = uniqueWishlistHandles(next, [handle]);
  }

  for (const handle of overlay.remove) {
    const key = guestHandleKey(handle);
    next = next.filter((item) => guestHandleKey(item) !== key);
  }

  return next;
}

function reconcileOptimisticOverlayWithLiquid(liquidHandles) {
  const overlay = getOptimisticOverlay();
  if (
    overlay.add.length === 0 &&
    overlay.remove.length === 0 &&
    overlay.settled.length === 0
  ) {
    return overlay;
  }

  const next = emptyOptimisticOverlay();

  for (const handle of overlay.add) {
    // Storefront tags can lag Admin tagsAdd — keep until Liquid reflects add.
    if (guestHandlesInclude(liquidHandles, handle)) continue;
    next.add.push(handle);
    if (guestHandlesInclude(overlay.settled, handle)) {
      next.settled.push(handle);
    }
  }

  for (const handle of overlay.remove) {
    // Keep until Liquid no longer has the tag.
    if (!guestHandlesInclude(liquidHandles, handle)) continue;
    next.remove.push(handle);
    if (guestHandlesInclude(overlay.settled, handle)) {
      next.settled.push(handle);
    }
  }

  return setOptimisticOverlay(next);
}

function hasOptimisticOverlayPending() {
  const overlay = getOptimisticOverlay();
  return overlay.add.length > 0 || overlay.remove.length > 0;
}

function isOptimisticOverlaySettled(handle) {
  return guestHandlesInclude(getOptimisticOverlay().settled, handle);
}

function hydrateLoggedInWishlistButtons(handles) {
  document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
    if (!button.dataset.customerId) return;
    const handle = button.dataset.modelFamilyHandle || '';
    if (!handle) return;

    const subscribed = guestHandlesInclude(handles, handle);
    button.dataset.wishlistIsSubscribed = subscribed ? 'true' : 'false';

    const addLabel = decodeHtmlEntities(button.dataset.labelAdd || '');
    const removeLabel = decodeHtmlEntities(button.dataset.labelRemove || '');
    const nextLabel = subscribed ? removeLabel : addLabel;
    const labelEl = button.querySelector('[data-wishlist-button-label]');
    const labelTarget = labelEl ?? button;
    labelTarget.textContent = nextLabel;
    button.setAttribute('aria-label', nextLabel);
  });
}

function resetWishlistHandlesFromLiquidAndOverlay() {
  const base = Array.isArray(liquidWishlistFamilyHandlesSnapshot)
    ? liquidWishlistFamilyHandlesSnapshot
    : Array.isArray(window.wishlistFamilyHandles)
      ? window.wishlistFamilyHandles
      : [];
  window.wishlistFamilyHandles = applyOptimisticOverlayToHandles(base);
  setWishlistFamilyCount(window.wishlistFamilyHandles.length);
  hydrateLoggedInWishlistButtons(window.wishlistFamilyHandles);
}

function retryOptimisticOverlayOps() {
  const endpoint = window.appRoutes?.wishlist_update;
  const customerId = window.customerId;
  if (!endpoint || !customerId) return;

  const overlay = getOptimisticOverlay();
  const ops = [
    ...overlay.add.map((handle) => ({ handle, mode: 'add' })),
    ...overlay.remove.map((handle) => ({ handle, mode: 'remove' })),
  ].filter(({ handle }) => !isOptimisticOverlaySettled(handle));

  for (const { handle, mode } of ops) {
    const key = `${mode}:${guestHandleKey(handle)}`;
    if (!guestHandleKey(handle) || optimisticOverlayRetryInflight.has(key)) {
      continue;
    }

    optimisticOverlayRetryInflight.set(
      key,
      (async () => {
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            keepalive: true,
            body: JSON.stringify({
              customerId,
              modelFamilyHandle: handle,
              mode,
              source: 'pdp',
            }),
          });
          if (!response.ok) throw new Error('Wishlist overlay retry failed');
          // Keep overlay until Liquid catches up; only mark settled so we
          // do not retry again while storefront tags still lag.
          markOptimisticOverlaySettled(handle);
        } catch (error) {
          console.error(error);
          clearOptimisticOverlay(handle);
          resetWishlistHandlesFromLiquidAndOverlay();
        } finally {
          optimisticOverlayRetryInflight.delete(key);
        }
      })(),
    );
  }
}

function initLoggedInWishlistOptimisticState() {
  if (!isCustomerLoggedIn()) return;

  // Capture Liquid handles once per full page load. Section reloads must not
  // treat an already-merged overlay list as the Liquid baseline.
  if (!Array.isArray(liquidWishlistFamilyHandlesSnapshot)) {
    liquidWishlistFamilyHandlesSnapshot = Array.isArray(
      window.wishlistFamilyHandles,
    )
      ? [...window.wishlistFamilyHandles]
      : [];
  }

  reconcileOptimisticOverlayWithLiquid(liquidWishlistFamilyHandlesSnapshot);
  window.wishlistFamilyHandles = applyOptimisticOverlayToHandles(
    liquidWishlistFamilyHandlesSnapshot,
  );
  setWishlistFamilyCount(window.wishlistFamilyHandles.length);
  hydrateLoggedInWishlistButtons(window.wishlistFamilyHandles);
  retryOptimisticOverlayOps();
}

function isCustomerLoggedIn() {
  return Boolean(
    window.customerId ||
      document.querySelector('[data-wishlist-icon][data-customer-logged-in="true"]') ||
      document.querySelector('[data-wishlist-page][data-customer-id]'),
  );
}

function getWishlistFamilyCount() {
  const bubble = document.querySelector('[data-wishlist-count-bubble]');
  if (bubble?.dataset.wishlistCountValue != null) {
    const fromBubble = Number.parseInt(bubble.dataset.wishlistCountValue, 10);
    if (!Number.isNaN(fromBubble)) return fromBubble;
  }

  const countEl = document.querySelector('[data-wishlist-count]');
  if (countEl) {
    const parsed = Number.parseInt(countEl.textContent, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const headingEl = document.querySelector('[data-wishlist-heading-count]');
  if (headingEl) {
    const fromHeading = Number.parseInt(headingEl.textContent, 10);
    if (!Number.isNaN(fromHeading)) return fromHeading;
  }

  return 0;
}

function setWishlistFamilyCount(nextCount) {
  const count = Math.max(0, Number(nextCount) || 0);
  const displayCount = count < 100 ? String(count) : '';

  document.querySelectorAll('[data-wishlist-count-bubble]').forEach((bubble) => {
    bubble.dataset.wishlistCountValue = String(count);
    toggleHidden(bubble, count === 0);

    const countEl = bubble.querySelector('[data-wishlist-count]');
    if (countEl) {
      countEl.textContent = displayCount;
    }

    const label = bubble.querySelector('[data-wishlist-count-label]');
    if (label) {
      const template =
        count === 1
          ? bubble.dataset.wishlistCountLabelOne
          : bubble.dataset.wishlistCountLabelOther;
      if (template) {
        label.textContent = template.replace(/__COUNT__/g, String(count));
      }
    }
  });

  // Fallback: update any count nodes not nested under a bubble.
  document.querySelectorAll('[data-wishlist-count]').forEach((countEl) => {
    if (countEl.closest('[data-wishlist-count-bubble]')) return;
    countEl.textContent = displayCount;
  });

  document.querySelectorAll('[data-wishlist-heading-count]').forEach((el) => {
    el.textContent = String(count);
  });

  document.querySelectorAll('[data-wishlist-heading-count-wrap]').forEach((wrap) => {
    toggleHidden(wrap, count === 0);
  });
}

function initWishlistFamilyCountBadge() {
  if (isCustomerLoggedIn()) return;
  setWishlistFamilyCount(getGuestWishlistHandles().length);
}

function showWishlistLoginBanner() {
  const banner = document.querySelector('[data-wishlist-login-banner]');
  if (!banner) return;
  toggleHidden(banner, false);
}

function initWishlistLoginBanner() {
  document
    .querySelector('[data-wishlist-login-banner-dismiss]')
    ?.addEventListener('click', () => {
      const banner = document.querySelector('[data-wishlist-login-banner]');
      toggleHidden(banner, true);
    });
}

/** Shared so page init and wishlist page await the same in-flight claim. */
let guestWishlistClaimPromise = null;

function claimGuestWishlistIfNeeded() {
  if (guestWishlistClaimPromise) return guestWishlistClaimPromise;

  const pendingEntries = getGuestWishlistEntries();
  // HARD STOP: empty localStorage → no fetch, no listeners for claim.
  if (pendingEntries.length === 0) {
    guestWishlistClaimPromise = Promise.resolve(false);
    return guestWishlistClaimPromise;
  }

  const pending = pendingEntries.map((entry) => entry.familyHandle);
  const customerId = window.customerId;
  const endpoint = window.appRoutes?.wishlist_update;
  if (!customerId || !endpoint) {
    guestWishlistClaimPromise = Promise.resolve(false);
    return guestWishlistClaimPromise;
  }

  guestWishlistClaimPromise = (async () => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          customerId,
          modelFamilyHandles: pending,
          entries: pendingEntries,
          source: 'guest-merge',
          mode: 'add',
        }),
      });

      if (!response.ok) throw new Error('Guest wishlist claim failed');
      const payload = await response.json().catch(() => null);
      if (!payload?.success) throw new Error('Guest wishlist claim rejected');

      try {
        window.localStorage?.removeItem(GUEST_WISHLIST_STORAGE_KEY);
      } catch (error) {
        console.error(error);
      }

      const liquidHandles = Array.isArray(window.wishlistFamilyHandles)
        ? window.wishlistFamilyHandles
        : [];
      const merged = applyOptimisticOverlayToHandles(
        uniqueWishlistHandles(liquidHandles, pending),
      );
      window.wishlistFamilyHandles = merged;
      setWishlistFamilyCount(merged.length);

      return true;
    } catch (error) {
      // Leave localStorage intact so the next page load (or same-page retry) can retry.
      console.error(error);
      guestWishlistClaimPromise = null;
      return false;
    }
  })();

  return guestWishlistClaimPromise;
}

window.NoosWishlist = {
  ...(window.NoosWishlist || {}),
  GUEST_WISHLIST_STORAGE_KEY,
  OPTIMISTIC_WISHLIST_OVERLAY_KEY,
  getGuestWishlistHandles,
  getGuestWishlistEntries,
  setGuestWishlistHandles,
  setGuestWishlistEntries,
  addGuestWishlistHandle,
  addGuestWishlistEntry,
  removeGuestWishlistHandle,
  getWishlistFamilyCount,
  setWishlistFamilyCount,
  showWishlistLoginBanner,
  claimGuestWishlistIfNeeded,
  getOptimisticOverlay,
  applyOptimisticOverlayToHandles,
};

function getWishlistPage(root = document) {
  if (root?.matches?.('[data-wishlist-page]')) return root;
  return (
    root?.querySelector?.('[data-wishlist-page]') ??
    document.querySelector('[data-wishlist-page]')
  );
}

function getWishlistTemplate(wishlistPage, templateName) {
  const scope = wishlistPage?.closest('.wishlist-page') ?? document;
  return scope.querySelector(`[data-wishlist-template="${templateName}"]`);
}

function setWishlistPageLoading(wishlistPage, isLoading) {
  if (!wishlistPage) return;
  toggleHidden(
    wishlistPage.querySelector('[data-wishlist-loading-state]'),
    !isLoading,
  );
  toggleHidden(
    wishlistPage.querySelector('[data-wishlist-products-loading-state]'),
    !isLoading,
  );
}

function syncWishlistEmptyStates(candidate) {
  const wishlistPage = getWishlistPage(candidate);
  if (!wishlistPage) return;

  const loadingState = wishlistPage.querySelector('[data-wishlist-loading-state]');
  const productsLoadingState = wishlistPage.querySelector(
    '[data-wishlist-products-loading-state]',
  );
  const errorState = wishlistPage.querySelector('[data-wishlist-error-state]');
  const body = wishlistPage.querySelector('[data-wishlist-body]');
  const productsContent = wishlistPage.querySelector(
    '[data-wishlist-products-content]',
  );
  const alertsList = wishlistPage.querySelector('[data-wishlist-alerts]');
  const alertsEmpty = wishlistPage.querySelector('[data-wishlist-alerts-empty]');
  const wishlistGrid = wishlistPage.querySelector('[data-wishlist-grid]');
  const productsEmptyNoAlerts = wishlistPage.querySelector(
    '[data-wishlist-products-empty-no-alerts]',
  );
  const productsEmptyNoMatches = wishlistPage.querySelector(
    '[data-wishlist-products-empty-no-matches]',
  );

  const isLoading = Boolean(
    (loadingState && !loadingState.hasAttribute('hidden')) ||
      (productsLoadingState && !productsLoadingState.hasAttribute('hidden')),
  );
  const hasError = Boolean(errorState && !errorState.hasAttribute('hidden'));

  if (isLoading || hasError) {
    toggleHidden(body, true);
    toggleHidden(productsContent, true);
    return;
  }

  const hasAlerts = Boolean(
    alertsList?.querySelector('[data-wishlist-alert-item]'),
  );
  const hasProducts = Boolean(
    wishlistGrid?.querySelector('[data-wishlist-item]'),
  );

  toggleHidden(body, false);
  toggleHidden(productsContent, false);
  toggleHidden(alertsList, !hasAlerts);
  toggleHidden(alertsEmpty, hasAlerts);
  toggleHidden(wishlistGrid, !hasProducts);
  toggleHidden(productsEmptyNoAlerts, hasAlerts);
  toggleHidden(productsEmptyNoMatches, !hasAlerts || hasProducts);
}

async function fetchWishlistPage(endpoint, { customerId, modelFamilyHandles } = {}) {
  const body = {};
  if (customerId) body.customerId = customerId;
  if (Array.isArray(modelFamilyHandles)) {
    body.modelFamilyHandles = modelFamilyHandles;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Wishlist request failed (${response.status})`);
  }

  const payload = await response.json();
  if (
    !payload?.success ||
    !Array.isArray(payload.modelFamilyHandles) ||
    !Array.isArray(payload.data)
  ) {
    throw new Error('Wishlist payload is invalid');
  }

  return {
    modelFamilyHandles: payload.modelFamilyHandles,
    data: payload.data,
  };
}

function renderWishlistAlerts(wishlistPage, modelFamilyHandles) {
  const alertsList = wishlistPage.querySelector('[data-wishlist-alerts]');
  const template = getWishlistTemplate(wishlistPage, 'alert');
  if (!alertsList || !template) return;

  const customerId = wishlistPage.dataset.customerId || '';
  const labelAdd = decodeHtmlEntities(wishlistPage.dataset.labelAdd || '');
  const labelRemove = decodeHtmlEntities(wishlistPage.dataset.labelRemove || '');

  alertsList.replaceChildren();

  for (const handle of modelFamilyHandles) {
    const fragment = template.content.cloneNode(true);
    const article = fragment.querySelector('[data-wishlist-alert-item]');
    const titleEl = fragment.querySelector('[data-wishlist-alert-title]');
    const button = fragment.querySelector('[data-wishlist-button]');
    const labelEl = fragment.querySelector('[data-wishlist-button-label]');

    if (!article || !titleEl || !button || !labelEl) continue;

    article.dataset.modelFamilyGroup = handle;
    titleEl.textContent = formatModelFamilyTitle(handle);
    button.dataset.customerId = customerId;
    button.dataset.modelFamilyHandle = handle;
    button.dataset.wishlistSource = 'wishlist-page';
    button.dataset.labelAdd = labelAdd;
    button.dataset.labelRemove = labelRemove;
    button.dataset.wishlistIsSubscribed = 'true';
    labelEl.textContent = labelRemove;
    button.setAttribute('aria-label', labelRemove);

    alertsList.appendChild(fragment);
  }
}

function populateWishlistProductItem(fragment, product) {
  const article = fragment.querySelector('[data-wishlist-item]');
  const link = fragment.querySelector('[data-wishlist-product-link]');
  const vendorEl = fragment.querySelector('[data-wishlist-product-vendor]');
  const imageWrap = fragment.querySelector('[data-wishlist-product-media-image]');
  const placeholderWrap = fragment.querySelector(
    '[data-wishlist-product-media-placeholder]',
  );
  const imageEl = fragment.querySelector('[data-wishlist-product-image]');
  const priceEl = fragment.querySelector('[data-wishlist-product-price]');
  const priceRegularEl = fragment.querySelector(
    '[data-wishlist-product-price-regular]',
  );
  const priceCompareEl = fragment.querySelector(
    '[data-wishlist-product-price-compare]',
  );
  const priceSaleEl = fragment.querySelector('[data-wishlist-product-price-sale]');

  if (!article || !link || !priceEl || !priceRegularEl || !priceSaleEl) {
    return;
  }

  const productUrl = productUrlFor(product);
  const priceMarkup = formatPriceRange(product.price);
  const compareAtMarkup = formatPriceRange(
    product.compareAtPrice,
    'minVariantCompareAtPrice',
    'maxVariantCompareAtPrice',
  );
  const showCompareAtPrice =
    compareAtMarkup &&
    compareAtMarkup !== priceMarkup &&
    product.compareAtPrice?.minVariantCompareAtPrice?.amount &&
    Number(product.compareAtPrice.minVariantCompareAtPrice.amount) >
      Number(product.price?.minVariantPrice?.amount);

  article.dataset.modelFamilyGroup = product.modelFamilyHandle || '';
  link.href = productUrl;
  link.textContent = product.title || '';

  if (vendorEl) {
    const vendor = product.vendor || '';
    vendorEl.textContent = vendor;
    toggleHidden(vendorEl, !vendor);
  }

  const rawImageUrl = product.featuredMedia?.url;
  if (rawImageUrl && imageWrap && imageEl && placeholderWrap) {
    imageEl.src = shopifyImageUrlWithWidth(rawImageUrl, 533);
    imageEl.srcset = wishlistFeaturedImageSrcSet(
      rawImageUrl,
      product.featuredMedia.width,
    );
    imageEl.alt = product.featuredMedia?.altText || product.title || '';

    if (product.featuredMedia.width) {
      imageEl.width = product.featuredMedia.width;
    } else {
      imageEl.removeAttribute('width');
    }

    if (product.featuredMedia.height) {
      imageEl.height = product.featuredMedia.height;
    } else {
      imageEl.removeAttribute('height');
    }

    toggleHidden(imageWrap, false);
    toggleHidden(placeholderWrap, true);
  } else if (imageWrap && placeholderWrap) {
    toggleHidden(imageWrap, true);
    toggleHidden(placeholderWrap, false);
  }

  priceEl.classList.toggle('price--on-sale', Boolean(showCompareAtPrice));
  priceRegularEl.textContent = priceMarkup;
  priceSaleEl.textContent = priceMarkup;

  if (priceCompareEl) {
    priceCompareEl.textContent = compareAtMarkup;
  }
}

function renderWishlistProducts(wishlistPage, products) {
  const wishlistGrid = wishlistPage.querySelector('[data-wishlist-grid]');
  const template = getWishlistTemplate(wishlistPage, 'product');
  if (!wishlistGrid || !template) return;

  wishlistGrid.replaceChildren();

  for (const product of products) {
    const fragment = template.content.cloneNode(true);
    populateWishlistProductItem(fragment, product);
    wishlistGrid.appendChild(fragment);
  }
}

async function initializeWishlistPage(root) {
  const wishlistPage = getWishlistPage(root);
  if (!wishlistPage || wishlistPage.dataset.initialized === 'true') return;

  wishlistPage.dataset.initialized = 'true';

  const errorState = wishlistPage.querySelector('[data-wishlist-error-state]');
  const customerId = wishlistPage.dataset.customerId || window.customerId || '';
  const wishlistEndpoint = window.appRoutes?.wishlist || '';
  const guestHandles = customerId ? null : getGuestWishlistHandles();

  if (!wishlistEndpoint) {
    setWishlistPageLoading(wishlistPage, false);
    toggleHidden(errorState, true);
    syncWishlistEmptyStates(wishlistPage);
    return;
  }

  // Guest with nothing saved — show empty states without hitting the API.
  if (!customerId && Array.isArray(guestHandles) && guestHandles.length === 0) {
    renderWishlistAlerts(wishlistPage, []);
    renderWishlistProducts(wishlistPage, []);
    setWishlistFamilyCount(0);
    setWishlistPageLoading(wishlistPage, false);
    toggleHidden(errorState, true);
    syncWishlistEmptyStates(wishlistPage);
    return;
  }

  try {
    setWishlistPageLoading(wishlistPage, true);
    toggleHidden(errorState, true);
    syncWishlistEmptyStates(wishlistPage);

    // Wait for guest claim so post-login wishlist shows merged tags.
    if (customerId) {
      await claimGuestWishlistIfNeeded();
    }

    // Prefer Liquid + pending guest + optimistic overlay over Admin tag fetch.
    const liquidHandles = Array.isArray(window.wishlistFamilyHandles)
      ? window.wishlistFamilyHandles
      : [];
    const pendingHandles = getGuestWishlistHandles();
    const clientHandles = applyOptimisticOverlayToHandles(
      uniqueWishlistHandles(liquidHandles, pendingHandles),
    );

    const payload = await fetchWishlistPage(wishlistEndpoint, {
      customerId: customerId || undefined,
      modelFamilyHandles: customerId ? clientHandles : guestHandles,
    });

    // Prefer local guest handles for alerts so remove buttons match storage keys.
    const alertHandles = customerId ? clientHandles : guestHandles;

    renderWishlistAlerts(wishlistPage, alertHandles);
    renderWishlistProducts(wishlistPage, payload.data);
    setWishlistFamilyCount(alertHandles.length);
    toggleHidden(errorState, true);
  } catch (error) {
    console.error(error);
    wishlistPage.querySelector('[data-wishlist-alerts]')?.replaceChildren();
    wishlistPage.querySelector('[data-wishlist-grid]')?.replaceChildren();
    toggleHidden(errorState, false);
  } finally {
    setWishlistPageLoading(wishlistPage, false);
    syncWishlistEmptyStates(wishlistPage);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define(
    'wishlist-button',
    class WishlistButton extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('[data-wishlist-button]');
        this.isLoading = false;
        this.variantChangeUnsubscriber = undefined;

        if (!this.button) return;

        this.hydrateGuestSubscriptionState();
        this.updateButtonLabel();
        this.button.addEventListener('click', this.onClick.bind(this));
      }

      connectedCallback() {
        if (
          this.variantChangeUnsubscriber ||
          typeof subscribe !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return;
        }

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const variant = event?.data?.variant;
            if (!variant || !this.button) return;
            if (this.button.dataset.wishlistSource !== 'pdp') return;

            if (variant.id != null) {
              this.button.dataset.variantId = String(variant.id);
            }
            if (variant.sku != null) {
              this.button.dataset.sku = String(variant.sku);
            } else {
              delete this.button.dataset.sku;
            }
          },
        );
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = undefined;
        }
      }

      get labelEl() {
        return this.button?.querySelector('[data-wishlist-button-label]');
      }

      get spinnerEl() {
        return this.button?.querySelector('.loading__spinner');
      }

      get customerId() {
        return this.button?.dataset.customerId || '';
      }

      get modelFamilyHandle() {
        return this.button?.dataset.modelFamilyHandle || '';
      }

      get wishlistSource() {
        return this.button?.dataset.wishlistSource || 'pdp';
      }

      get entryContext() {
        return {
          familyHandle: this.modelFamilyHandle,
          vendor: nullableDatasetString(this.button?.dataset.vendor),
          productId: nullableDatasetString(this.button?.dataset.productId),
          variantId: nullableDatasetString(this.button?.dataset.variantId),
          sku: nullableDatasetString(this.button?.dataset.sku),
        };
      }

      get isGuest() {
        return !this.customerId;
      }

      setLoading(isLoading) {
        if (!this.button) return;

        this.button.classList.toggle('loading', isLoading);
        this.button.toggleAttribute('aria-disabled', isLoading);
        this.button.toggleAttribute('aria-busy', isLoading);
        this.button.disabled = isLoading;

        if (isLoading) {
          this.spinnerEl?.classList.remove('hidden');
          this.labelEl?.setAttribute('hidden', '');
        } else {
          this.spinnerEl?.classList.add('hidden');
          this.labelEl?.removeAttribute('hidden');
        }
      }

      get isSubscribed() {
        return this.button?.dataset.wishlistIsSubscribed === 'true';
      }

      set isSubscribed(value) {
        if (!this.button) return;
        this.button.dataset.wishlistIsSubscribed = value ? 'true' : 'false';
      }

      get endpoint() {
        return window.appRoutes?.wishlist_update;
      }

      get payload() {
        const context = this.entryContext;
        return {
          customerId: this.customerId,
          modelFamilyHandle: this.modelFamilyHandle,
          mode: this.isSubscribed ? 'remove' : 'add',
          source: this.wishlistSource,
          vendor: context.vendor,
          productId: context.productId,
          variantId: context.variantId,
          sku: context.sku,
        };
      }

      hydrateGuestSubscriptionState() {
        if (!this.isGuest || !this.modelFamilyHandle) return;
        this.isSubscribed = guestHandlesInclude(
          getGuestWishlistHandles(),
          this.modelFamilyHandle,
        );
      }

      updateButtonLabel() {
        if (!this.button) return;
        const addLabel = decodeHtmlEntities(this.button.dataset.labelAdd || '');
        const removeLabel = decodeHtmlEntities(
          this.button.dataset.labelRemove || '',
        );
        const nextLabel = this.isSubscribed ? removeLabel : addLabel;
        const labelTarget = this.labelEl ?? this.button;
        labelTarget.textContent = nextLabel;
        this.button.setAttribute('aria-label', nextLabel);
      }

      onGuestClick() {
        const handle = this.modelFamilyHandle;
        if (!handle) return;

        const wasSubscribed = this.isSubscribed;

        if (wasSubscribed) {
          const nextHandles = removeGuestWishlistHandle(handle);
          this.isSubscribed = guestHandlesInclude(nextHandles, handle);
        } else {
          const nextEntries = addGuestWishlistEntry(this.entryContext);
          this.isSubscribed = guestHandlesInclude(
            nextEntries.map((entry) => entry.familyHandle),
            handle,
          );
          if (this.isSubscribed) showWishlistLoginBanner();
        }

        this.updateButtonLabel();

        if (
          this.button.dataset.wishlistRemoveOnly === 'true' &&
          wasSubscribed &&
          !this.isSubscribed
        ) {
          this.removeFromWishlistPage();
        }
      }

      onClick() {
        if (!this.button || this.isLoading) return;

        if (this.isGuest) {
          this.onGuestClick();
          return;
        }

        if (!this.endpoint || !this.modelFamilyHandle) return;

        this.isLoading = true;
        this.setLoading(true);

        const wasSubscribed = this.isSubscribed;
        const mode = wasSubscribed ? 'remove' : 'add';
        const handle = this.modelFamilyHandle;
        const context = this.entryContext;
        const requestBody = {
          customerId: this.customerId,
          modelFamilyHandle: handle,
          mode,
          source: this.wishlistSource,
          vendor: context.vendor,
          productId: context.productId,
          variantId: context.variantId,
          sku: context.sku,
        };

        // Optimistic UI — flip before the network round-trip.
        this.isSubscribed = !wasSubscribed;
        this.updateButtonLabel();
        recordOptimisticOverlay(handle, mode);
        syncWishlistFamilyHandlesCache(handle, mode);
        setWishlistFamilyCount(
          (Array.isArray(window.wishlistFamilyHandles)
            ? window.wishlistFamilyHandles
            : []
          ).length,
        );

        if (
          this.button.dataset.wishlistRemoveOnly === 'true' &&
          wasSubscribed &&
          !this.isSubscribed
        ) {
          this.removeFromWishlistPage();
        }

        fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          keepalive: true,
          body: JSON.stringify(requestBody),
        })
          .then((response) => {
            if (!response.ok) throw new Error('Wishlist update failed');
            // Keep overlay until storefront customer.tags catch up. Clearing
            // here made the next page show the old Liquid badge count.
            markOptimisticOverlaySettled(handle);
          })
          .catch((error) => {
            console.error(error);
            clearOptimisticOverlay(handle);
            this.isSubscribed = wasSubscribed;
            this.updateButtonLabel();
            syncWishlistFamilyHandlesCache(
              handle,
              mode === 'add' ? 'remove' : 'add',
            );
            setWishlistFamilyCount(
              (Array.isArray(window.wishlistFamilyHandles)
                ? window.wishlistFamilyHandles
                : []
              ).length,
            );
          })
          .finally(() => {
            this.isLoading = false;
            this.setLoading(false);
          });
      }

      removeFromWishlistPage() {
        const modelFamilyHandle = this.button.dataset.modelFamilyHandle;
        const wishlistPage = getWishlistPage(
          this.closest('[data-wishlist-page]') ?? document,
        );
        const wishlistGrid = wishlistPage?.querySelector('[data-wishlist-grid]');
        const normalizedHandle = String(modelFamilyHandle || '')
          .trim()
          .toLowerCase();

        if (wishlistGrid && normalizedHandle) {
          wishlistGrid
            .querySelectorAll('[data-wishlist-item][data-model-family-group]')
            .forEach((wishlistItem) => {
              const group = String(
                wishlistItem.dataset.modelFamilyGroup || '',
              )
                .trim()
                .toLowerCase();
              if (group === normalizedHandle) wishlistItem.remove();
            });
        }

        const alertArticle = this.closest('[data-wishlist-alert-item]');
        if (alertArticle) {
          alertArticle.remove();
        } else {
          this.closest('[data-wishlist-item]')?.remove();
        }

        syncWishlistEmptyStates(wishlistPage);
      }
    },
  );
}

// Start claim early; wishlist page awaits the same promise before fetching.
claimGuestWishlistIfNeeded();
initWishlistFamilyCountBadge();
initLoggedInWishlistOptimisticState();
initWishlistLoginBanner();
initializeWishlistPage(document);
document.addEventListener('shopify:section:load', (event) => {
  initializeWishlistPage(event.target);
  initWishlistFamilyCountBadge();
  initLoggedInWishlistOptimisticState();
});
